import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Swal from 'sweetalert2';
import './Register.css';

function CompleteRegistration() {
    const [designations, setDesignations] = useState([]);
    const [formData, setFormData] = useState({
        phone_number: '',
        designation: ''
    });
    const [loading, setLoading] = useState(false);
    
    const { user, refreshUser, onboardingStage, onboardingPending, onboardingPath, setOnboarding } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        if (!onboardingPending) {
            navigate('/dashboard');
            return;
        }

        if (onboardingStage !== 'complete_registration') {
            navigate(onboardingPath || '/create-company-profile');
            return;
        }

        fetchDesignations();

        // Pre-fill form with existing user data
        if (user) {
            setFormData({
                phone_number: user.phone_number || '',
                designation: user.designation || ''
            });
        }
    }, [user, onboardingPending, onboardingStage, onboardingPath, navigate]);

    const fetchDesignations = async () => {
        try {
            const response = await api.get('/designations');
            setDesignations(response.data.data);
        } catch (error) {
            console.error('Failed to fetch designations:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await api.post('/complete-registration', formData);
            
            // Refresh user data
            await refreshUser();
            setOnboarding(response.data.user?.onboarding);

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: response.data.message,
                timer: 2000
            });

            navigate(response.data.user?.onboarding?.path || '/create-company-profile');
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to complete registration';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message
            });
        } finally {
            setLoading(false);
        }
    };

    if (!user || !onboardingPending || onboardingStage !== 'complete_registration') {
        return null;
    }

    return (
        <div className="register-container">
            <div className="register-content">
                <div className="register-form-section">
                    <div className="register-card">
                        <div className="logo-container">
                            <a href="/">
                                <img src="/assets/img/logos/new-logo.png" alt="Logo" width="180" />
                            </a>
                        </div>

                        <h3 className="register-title">Complete Registration</h3>

                        <form onSubmit={handleSubmit} className="register-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={user.first_name}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Last Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={user.last_name}
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={user.email}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Phone Number</label>
                                    <input
                                        type="text"
                                        name="phone_number"
                                        className="form-control"
                                        value={formData.phone_number}
                                        onChange={handleChange}
                                        placeholder="Phone"
                                        maxLength="10"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Designation</label>
                                    <select
                                        name="designation"
                                        className="form-control"
                                        value={formData.designation}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Designation</option>
                                        {designations.map(designation => (
                                            <option key={designation.id} value={designation.id}>
                                                {designation.designation_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div></div>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-submit"
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Get Started'}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="register-info-section">
                    <div className="info-content">
                        <h1>"Create a Cedur account"</h1>
                        <h3>It's free to set up your company with Cedur.</h3>
                        <hr />
                        <p>Not ready to create an account?</p>
                        <a href="/features">Learn how Cedur works</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CompleteRegistration;
